import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { MapPin, Calendar, Star, MessageCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import VerificationBadge from './VerificationBadge';

/**
 * ProfileCard - User profile preview card component
 * Displays user information with avatar, verification badges, and actions
 * 
 * @param {Object} props
 * @param {string} props.name - User's full name
 * @param {string} props.username - User's username
 * @param {string} props.avatarUrl - User's avatar image URL
 * @param {string} props.bio - User's bio/description
 * @param {string} props.location - User's location
 * @param {string} props.memberSince - Member since date
 * @param {number} props.rating - User rating (0-5)
 * @param {number} props.reviewCount - Number of reviews
 * @param {number} props.eventsHosted - Number of events hosted
 * @param {number} props.eventsAttended - Number of events attended
 * @param {Array} props.verifications - Array of verification types
 * @param {boolean} props.isPremium - Whether user is premium member
 * @param {Function} props.onViewProfile - Callback when view profile is clicked
 * @param {Function} props.onMessage - Callback when message button is clicked
 * @param {string} props.variant - Card variant: 'default', 'compact'
 * @param {string} props.className - Additional CSS classes
 */
const ProfileCard = ({
  name,
  username,
  avatarUrl,
  bio,
  location,
  memberSince,
  rating,
  reviewCount,
  eventsHosted,
  eventsAttended,
  verifications = [],
  isPremium = false,
  onViewProfile,
  onMessage,
  variant = 'default',
  className,
  ...props
}) => {
  // Get initials for avatar fallback
  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };

  const isCompact = variant === 'compact';

  return (
    <Card 
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-soft",
        isPremium && "ring-2 ring-secondary-500",
        className
      )}
      {...props}
    >
      <CardHeader className={cn("text-center", isCompact && "pb-3")}>
        {/* Avatar with verification ring */}
        <div className="flex justify-center mb-3">
          <div className={cn(
            "relative",
            verifications.length > 0 && "ring-4 ring-success-500 rounded-full"
          )}>
            <Avatar className={cn(
              isCompact ? "w-16 h-16" : "w-24 h-24",
              "border-4 border-background"
            )}>
              <AvatarImage src={avatarUrl} alt={name} />
              <AvatarFallback className="bg-primary-500 text-primary-foreground text-lg font-semibold">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            {isPremium && (
              <div className="absolute -bottom-1 -right-1 bg-secondary-500 rounded-full p-1.5 border-2 border-background">
                <Star className="w-4 h-4 text-secondary-foreground fill-current" />
              </div>
            )}
          </div>
        </div>

        {/* Name and Username */}
        <div className="space-y-1">
          <h3 className="text-display text-xl font-semibold text-primary-700">
            {name}
          </h3>
          {username && (
            <p className="text-sm text-neutral-500">@{username}</p>
          )}
        </div>

        {/* Verification Badges */}
        {verifications.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mt-3">
            {verifications.map((type, index) => (
              <VerificationBadge
                key={index}
                type={type}
                size="sm"
                variant="outline"
              />
            ))}
          </div>
        )}

        {/* Rating */}
        {rating !== undefined && (
          <div className="flex items-center justify-center gap-1 mt-2">
            <Star className="w-4 h-4 text-secondary-500 fill-current" />
            <span className="font-semibold text-primary-700">{rating.toFixed(1)}</span>
            {reviewCount !== undefined && (
              <span className="text-sm text-neutral-500">({reviewCount} reviews)</span>
            )}
          </div>
        )}
      </CardHeader>

      {!isCompact && (
        <CardContent className="space-y-3">
          {/* Bio */}
          {bio && (
            <CardDescription className="text-center text-sm line-clamp-3">
              {bio}
            </CardDescription>
          )}

          {/* Location */}
          {location && (
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-600">
              <MapPin className="w-4 h-4 text-primary-500" />
              <span>{location}</span>
            </div>
          )}

          {/* Member Since */}
          {memberSince && (
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-600">
              <Calendar className="w-4 h-4 text-primary-500" />
              <span>Member since {memberSince}</span>
            </div>
          )}

          {/* Stats */}
          {(eventsHosted !== undefined || eventsAttended !== undefined) && (
            <div className="flex justify-center gap-6 pt-3 border-t">
              {eventsHosted !== undefined && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-700">{eventsHosted}</div>
                  <div className="text-xs text-neutral-500">Events Hosted</div>
                </div>
              )}
              {eventsAttended !== undefined && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-700">{eventsAttended}</div>
                  <div className="text-xs text-neutral-500">Events Attended</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}

      <CardFooter className={cn("flex gap-2", isCompact && "pt-0")}>
        {onViewProfile && (
          <Button 
            variant="outline" 
            className="flex-1 border-primary-500 text-primary-500 hover:bg-primary-50"
            onClick={onViewProfile}
          >
            View Profile
          </Button>
        )}
        {onMessage && (
          <Button 
            className="flex-1 bg-secondary-500 hover:bg-secondary-600 text-secondary-foreground"
            onClick={onMessage}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Message
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default ProfileCard;